import React, { useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ChevronRight, Minus, Plus, ShoppingCart, Check } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { products } from '../data/mockData';

// Extended products data
const allProducts = [
  ...products,
  {
    id: 5,
    name: "Hoodie",
    description: "Premium heavyweight hoodie, hand-pressed with vibrant, long-lasting color. Stay warm in style.",
    price: 55.00,
    category: "apparel",
    image: "https://royal-ink-shop.lovable.app/assets/emperor-hoodie-CGfXIoPu.png",
    customizable: true
  },
  {
    id: 6,
    name: "Custom Mug",
    description: "Premium ceramic mug with UVDTF printing. Perfect for your morning coffee.",
    price: 18.00,
    category: "drinkware",
    image: "https://royal-ink-shop.lovable.app/assets/uvdtf-mug-CKugI6ad.jpg",
    customizable: true
  },
  {
    id: 7,
    name: "Ballcap",
    description: "Premium baseball cap with embroidered or printed design. Classic snapback style for any occasion.",
    price: 28.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1588850561407-ed78c282e89b?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 8,
    name: "Skullcap / Beanie",
    description: "Cozy knit beanie with custom embroidery options. Perfect for cooler weather.",
    price: 22.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1576871337632-b9aef4c17ab9?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 9,
    name: "Canvas Tote Bag",
    description: "Durable canvas tote bag with custom print. Eco-friendly and stylish for everyday use.",
    price: 25.00,
    category: "accessories",
    image: "https://images.unsplash.com/photo-1548863227-3af567fc3b27?w=600&h=400&fit=crop",
    customizable: true
  },
  {
    id: 10,
    name: "Emperor Collection Tee",
    description: "Exclusive Royal Wear collection. Premium cotton with signature Emperor design.",
    price: 40.00,
    category: "royal-wear",
    image: "https://royal-ink-shop.lovable.app/assets/apparel-mockup-1-CpSHoLEe.png",
    customizable: false
  }
];

const sizes = ['XS', 'S', 'M', 'L', 'XL', '2XL', '3XL'];
const colors = [
  { name: 'Black', value: '#000000' },
  { name: 'White', value: '#FFFFFF' },
  { name: 'Navy', value: '#1e3a5f' },
  { name: 'Red', value: '#dc2626' },
  { name: 'Gray', value: '#6b7280' }
];

const ProductPage = () => {
  const { id } = useParams();
  const product = allProducts.find(p => p.id === parseInt(id)) || allProducts[0];
  
  const [quantity, setQuantity] = useState(1);
  const [selectedSize, setSelectedSize] = useState('M');
  const [selectedColor, setSelectedColor] = useState(colors[0]);
  const [addedToCart, setAddedToCart] = useState(false);

  const handleAddToCart = () => {
    // Get existing cart from localStorage
    const existingCart = JSON.parse(localStorage.getItem('royalInkCart') || '[]');
    
    // Create cart item
    const cartItem = {
      product_id: product.id,
      name: product.name,
      price: product.price,
      quantity: quantity,
      size: product.category === 'apparel' ? selectedSize : null,
      color: product.category === 'apparel' ? selectedColor.name : null,
      image: product.image
    };
    
    // Check if item already exists in cart
    const existingIndex = existingCart.findIndex(
      item => item.product_id === product.id && 
              item.size === cartItem.size && 
              item.color === cartItem.color
    );
    
    if (existingIndex >= 0) {
      // Update quantity
      existingCart[existingIndex].quantity += quantity;
    } else {
      // Add new item
      existingCart.push(cartItem);
    }
    
    // Save to localStorage
    localStorage.setItem('royalInkCart', JSON.stringify(existingCart));
    
    // Show feedback
    setAddedToCart(true);
    setTimeout(() => setAddedToCart(false), 2000);
  };

  return (
    <div className="min-h-screen bg-white">
      <Header />
      
      <main className="py-8 md:py-12">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-8">
            <Link to="/" className="hover:text-gray-900 transition-colors">Home</Link>
            <ChevronRight className="w-4 h-4" />
            <Link to="/shop" className="hover:text-gray-900 transition-colors">Shop</Link>
            <ChevronRight className="w-4 h-4" />
            <span className="text-gray-900 font-medium">{product.name}</span>
          </nav>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Product Image */}
            <div className="bg-gray-50 rounded-2xl overflow-hidden aspect-square">
              <img
                src={product.image}
                alt={product.name}
                className="w-full h-full object-cover"
              />
            </div>

            {/* Product Info */}
            <div className="flex flex-col">
              <span className="text-amber-600 font-medium text-sm uppercase tracking-wider mb-2">
                {product.category}
              </span>
              <h1 className="font-serif text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                {product.name}
              </h1>
              <p className="text-gray-500 text-lg leading-relaxed mb-6">
                {product.description}
              </p>
              <div className="font-serif text-3xl font-bold text-amber-600 mb-8">
                ${product.price.toFixed(2)}
              </div>

              {/* Size Selection */}
              {product.category === 'apparel' && (
                <div className="mb-6">
                  <h3 className="font-medium text-gray-900 mb-3">Size</h3>
                  <div className="flex flex-wrap gap-2">
                    {sizes.map((size) => (
                      <button
                        key={size}
                        onClick={() => setSelectedSize(size)}
                        className={`px-4 py-2 border rounded-lg text-sm font-medium transition-all ${
                          selectedSize === size
                            ? 'border-amber-500 bg-amber-50 text-amber-700'
                            : 'border-gray-200 text-gray-600 hover:border-gray-300'
                        }`}
                      >
                        {size}
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Color Selection */}
              {product.category === 'apparel' && (
                <div className="mb-8">
                  <h3 className="font-medium text-gray-900 mb-3">Color: {selectedColor.name}</h3>
                  <div className="flex flex-wrap gap-3">
                    {colors.map((color) => (
                      <button
                        key={color.name}
                        onClick={() => setSelectedColor(color)}
                        className={`w-10 h-10 rounded-full border-2 transition-all ${
                          selectedColor.name === color.name
                            ? 'border-amber-500 ring-2 ring-amber-200'
                            : 'border-gray-200'
                        }`}
                        style={{ backgroundColor: color.value }}
                        title={color.name}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* Quantity */}
              <div className="mb-8">
                <h3 className="font-medium text-gray-900 mb-3">Quantity</h3>
                <div className="flex items-center gap-4">
                  <div className="flex items-center border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-3 hover:bg-gray-50 transition-colors"
                    >
                      <Minus className="w-4 h-4 text-gray-600" />
                    </button>
                    <span className="w-12 text-center font-medium text-gray-900">
                      {quantity}
                    </span>
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-3 hover:bg-gray-50 transition-colors"
                    >
                      <Plus className="w-4 h-4 text-gray-600" />
                    </button>
                  </div>
                  <span className="text-gray-500 text-sm">
                    ${(product.price * quantity).toFixed(2)} total
                  </span>
                </div>
              </div>

              {/* Add to Cart */}
              <Button
                onClick={handleAddToCart}
                className={`w-full py-6 text-base font-semibold rounded-lg transition-all duration-300 ${
                  addedToCart
                    ? 'bg-green-500 hover:bg-green-600 text-white'
                    : 'bg-amber-500 hover:bg-amber-600 text-gray-900'
                }`}
              >
                {addedToCart ? (
                  <>
                    <Check className="mr-2 h-5 w-5" />
                    Added to Cart
                  </>
                ) : (
                  <>
                    <ShoppingCart className="mr-2 h-5 w-5" />
                    Add to Cart
                  </>
                )}
              </Button>

              {product.customizable && (
                <p className="text-center text-gray-500 text-sm mt-4">
                  This item is customizable. Contact us for custom designs!
                </p>
              )}
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default ProductPage;
